import React from 'react'
import { useState, useEffect } from 'react'
import ReactECharts from 'echarts-for-react'
import CardChart from '../Components/CardChart'
import ListButton from '../Components/ListButton'
import { faCommentDots } from '@fortawesome/pro-solid-svg-icons'
import { Box, Table, TableBody, TableRow, TableCell, Skeleton, Tooltip, Typography } from '@mui/material'
import {
  ContentTooltiptLearningContinuumInfo,
  useStyles
} from '../../../../Assessment/Classes/MarkingClasses/ContentTooltip/ContentTooltip'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

const SkillsSnapshot = ({ isLoadingDashboard, isLoadingCurriculum, data }) => {
  const classes = useStyles()
  const curriculum = useSelector((state) => state?.assessment?.curriculum || [])

  const navigate = useNavigate()
  const [currentClass, setCurrentClass] = useState(null)
  const [currentUL, setCurrentUL] = useState(null)
  const [lcs, setLcs] = useState([])
  useEffect(() => {
    if (data) {
      const firstClass = data?.classes?.[0] || null
      setCurrentClass(firstClass)
      const firstUL = firstClass?.unitOfLearnings?.[0] || null
      setCurrentUL(firstUL)
    }
  }, [data])

  useEffect(() => {
    if (currentUL && currentClass) {
      const classId = currentClass?._id
      const ulId = currentUL?._id
      const filteredLcs = data?.lcs?.filter((lc) => lc.classId === classId && lc.ulId === ulId) || []
      setLcs(filteredLcs)
    }
  }, [currentClass, currentUL, data])

  const handleRowClick = (lc) => {
    navigate(
      `/dashboard/classes/${lc.curriculumId}/preview/${lc.classId}/unit-of-learning/${lc.ulId}/student-view`
    )
  }

  return (
    <CardChart
      icon={'chart-line'}
      title=' Skills Snapshot'
      bgColor='#fff'
      border='#c3c3c4'
      isLoading={isLoadingDashboard || isLoadingCurriculum}
    >
      <ListButton
        isLoading={isLoadingDashboard || isLoadingCurriculum}
        data={data?.classes || []}
        onClick={(cls) => {
          setCurrentClass(cls)
          const firstUL = cls?.unitOfLearnings?.[0] || null
          setCurrentUL(firstUL)
        }}
        module='classes'
        selectedId={currentClass?._id}
        cols={3}
      />
      <ListButton
        isLoading={isLoadingDashboard || isLoadingCurriculum}
        data={currentClass?.unitOfLearnings || []}
        onClick={(ul) => setCurrentUL(ul)}
        module='unit of learnings'
        selectedId={currentUL?._id}
        cols={5}
      />
      <Box className=' py-2 text-sm'>
        <Table>
          <TableBody>
            {isLoadingDashboard || isLoadingCurriculum ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell width={'220px'}>
                    <Box className='flex flex-wrap gap-2 items-center'>
                      <Skeleton width={10} height={30} />
                      <Skeleton width={70} height={30} />
                      <Skeleton width={40} height={30} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box className='flex gap-2 items-center'>
                        <Skeleton width={'10%'} height={30} />
                        <Skeleton width={'90%'} height={30} />
                      </Box>
                      <Box className='flex gap-2 items-center'>
                        <Skeleton width={'10%'} height={30} />
                        <Skeleton width={'90%'} height={30} />
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : lcs?.length > 0 ? (
              lcs?.map((lc) => {
                const currentCurriculum = curriculum?.find((cur) => cur._id === lc.curriculumId) || {}
                const lcElement = currentCurriculum?.lcElements?.children?.sort(
                  (a, b) => b.position - a.position
                )[0]
                const levels = currentCurriculum?.levels || []
                const marking = currentCurriculum?.marking || []
                const teacherValue = Number(lc?.maxLevelTeacher?.levelPosition ?? -1)
                const studentValue = Number(lc?.maxLevelStudent?.levelPosition ?? -1)
                const normalizeChartValue = (value) => {
                  const numericValue = Number(value ?? -1)
                  return numericValue >= 0 ? numericValue + 1 : 0
                }
                const teacherMarking =
                  lc?.maxLevelTeacher?.completedTeacher &&
                  marking?.find((m) => String(m._id) === String(lc.maxLevelTeacher.completedTeacher))
                    ? marking.find((m) => String(m._id) === String(lc.maxLevelTeacher.completedTeacher))
                    : {}
                const studentMarking =
                  lc?.maxLevelStudent?.completedStudent &&
                  marking?.find((m) => String(m._id) === String(lc.maxLevelStudent.completedStudent))
                    ? marking.find((m) => String(m._id) === String(lc.maxLevelStudent.completedStudent))
                    : {}
                const validPositions = [
                  ...levels
                    .map((level) => Number(level?.position ?? -1))
                    .filter((pos) => Number.isFinite(pos) && pos >= 0)
                    .map((pos) => pos + 1),
                  teacherValue >= 0 ? teacherValue + 1 : null,
                  studentValue >= 0 ? studentValue + 1 : null
                ].filter((value) => value !== null && Number.isFinite(value))
                const rangeMax = validPositions.length ? Math.max(...validPositions) : 1
                const rangeMin = 0
                const getLevelNameByPosition = (value) => {
                  const normalizedValue = Number(value ?? -1)
                  const match = levels.find((level) => Number(level?.position ?? 0) === normalizedValue)
                  return match?.name || ''
                }
                const levelNameByPosition = levels.reduce((acc, level) => {
                  const position = Number(level?.position ?? 0)
                  if (position >= 0) acc[position] = level?.name || ''
                  return acc
                }, {})
                const chartData = [
                  {
                    label: 'Teacher',
                    value: normalizeChartValue(teacherValue),
                    color: teacherMarking.primaryColor || '#f1f1f1',
                    name: teacherValue >= 0 ? getLevelNameByPosition(teacherValue) : ''
                  },
                  {
                    label: 'Student',
                    value: normalizeChartValue(studentValue),
                    color: studentMarking.primaryColor || '#f1f1f1',
                    name: studentValue >= 0 ? getLevelNameByPosition(studentValue) : ''
                  }
                ]

                return (
                  <TableRow key={lc._id} onClick={() => handleRowClick(lc)} sx={{ cursor: 'pointer' }}>
                    <TableCell width={'220px'}>
                      <Tooltip
                        arrow
                        placement='top'
                        id={`guttman-chart-table-lc-lc-${lc._id}`}
                        title={
                          <ContentTooltiptLearningContinuumInfo
                            lc={lc}
                            currentCurriculum={currentCurriculum}
                          />
                        }
                        classes={{ tooltip: classes.customWidthGuttmanChart }}
                      >
                        <Typography
                          style={{
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                          className=' text-sm flex items-center max-w-200px min-w-200px justify-start'
                        >
                          {lc.lcElements[lcElement?._id] || 'None'}
                        </Typography>
                        {/* {lc._id} */}
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ height: 60 }}>
                        <ReactECharts
                          option={{
                            backgroundColor: 'transparent',
                            grid: {
                              left: 10,
                              right: 10,
                              top: 8,
                              bottom: 12,
                              containLabel: true
                            },
                            xAxis: {
                              type: 'value',
                              min: rangeMin,
                              max: rangeMax,
                              show: true,
                              splitLine: { show: false },
                              axisLine: { show: false },
                              axisTick: { show: false },
                              axisLabel: { show: false }
                            },
                            yAxis: {
                              type: 'category',
                              data: chartData.map((item) => item.label),
                              axisLine: { show: false },
                              axisTick: { show: false },
                              axisLabel: {
                                color: '#667085',
                                fontSize: 10,
                                margin: 18
                              }
                            },
                            series: [
                              {
                                type: 'bar',
                                barWidth: 15,
                                barCategoryGap: '25%',
                                showBackground: true,
                                backgroundStyle: {
                                  color: 'rgba(15, 23, 42, 0.06)',
                                  borderRadius: [4, 4, 4, 4]
                                },
                                data: chartData.map((item, index) => ({
                                  value: item.value,
                                  name: item.label,
                                  itemStyle: {
                                    color: item.color,
                                    borderRadius: [0, 3, 3, 0]
                                  },
                                  labelName: item.name,
                                  label: {
                                    show: item.value >= 0,
                                    position: 'right',
                                    color: '#344054',
                                    fontSize: 10,
                                    align: item.value === levels.length ? 'right' : 'left',
                                    verticalAlign: 'middle',
                                    formatter: () => item.name || ''
                                  }
                                }))
                              }
                            ]
                          }}
                          style={{ height: '100%', width: '100%' }}
                          opts={{ renderer: 'svg' }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={2}>No learning content available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </CardChart>
  )
}

export default SkillsSnapshot
